<template>
    <div class="field--tax-residence">
        <div class="row custom-select-wrapper">
            <TheCountrySelect
                v-model="formData.taxCountry"
                :name="name + 'TaxCountry'"
                :country-list="countries"
                :label="$t(`${TRANSLATION_KEY}.chooseTaxCountryResidence`)"
                :placeholder="
                    $t(`${TRANSLATION_KEY}.chooseTaxCountryResidence`)
                "
                :default-value="formData.taxCountry || ''"
                :input-id="name + 'TaxCountry'"
                class="custom-select"
                :country-disable="countryDisable"
                @selected-country="formData.taxCountry = $event"
            />
        </div>
        <PolandTaxResidenceField
            v-if="isPolandTaxCountry"
            :name="name"
            :tax-office-data-foreigner="taxOfficeDataForeigner"
            :is-poland-tax-country="isPolandTaxCountry"
            @input="receiveDataPL($event)"
        />

        <div v-if="!isPolandTaxCountry">
            <div class="row">
                <div
                    v-if="
                        formData.taxCountry &&
                        tinCountries.includes(formData.taxCountry) &&
                        formData.taxCountry.length > 0
                    "
                    class="personal-id-number-description"
                >
                    {{ $t(`${TRANSLATION_KEY}.Tin${formData.taxCountry}`) }}
                </div>
                <div v-else class="personal-id-number-description">
                    {{ $t(`${TRANSLATION_KEY}.tinNumberPlaceholder`) }}
                </div>

                <Field
                    v-if="!formData.noTinNumber"
                    type="text"
                    :name="name + 'TinNumber'"
                    :disabled="formData.emptyTinNumber ? true : false"
                    :class="
                        formData.emptyTinNumber ? 'empty-tin-number valid' : ''
                    "
                    :model-value="formData.tinNumber ?? ''"
                    :placeholder="$t(`${TRANSLATION_KEY}.number`)"
                    cy-data="tin"
                    :is-numeric="isNumericalTIN"
                    :force-uppercase="true"
                    :remove-whitespace="true"
                    :no-auto-validation="true"
                    @update:model-value="formData.tinNumber = $event || null"
                    @blur="emitValidateTin"
                />
            </div>

            <!-- <div class="row" v-if="tinIgnoreCountries.includes(formData.taxCountry)">
        <CheckBox :name="name + 'EmptyTinNumber'" v-model="formData.emptyTinNumber" class="valid" cy-data="empty-tin" @input="addEmptyTinValue">
          <span class="is-exist-tin-number">
            {{ $t(`${TRANSLATION_KEY}.emptyTinLabel`) }}
          </span>
        </CheckBox>
        <span class="empty-tin-desc">{{ $t(`${TRANSLATION_KEY}.emptyTinDescription`) }}</span>
      </div> -->

            <NoTinField
                v-if="
                    formData.taxCountry &&
                    formData.taxCountry.length > 0 &&
                    !tinCountries.includes(formData.taxCountry)
                "
                :name="name + 'NoTinNumber'"
                @input="mergeFormData"
            />
        </div>
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    reactive,
    computed,
    watch,
    onMounted,
} from 'vue';
import { useStore } from 'vuex';
import Field from '@/components/basic/fields/Field.vue';
import NoTinField from '@/components/compositeFields/NoTinField.vue';
import TheCountrySelect from '@/components/basic/fields/TheCountrySelect.vue';
import PolandTaxResidenceField from '@/components/compositeFields/PolandTaxResidenceField.vue';
import CheckBox from '../basic/fields/CheckBox.vue';

interface TaxResidenceFormData {
    taxCountry: string | null;
    tinNumber: string | null;
    noTinNumber: string | null;
    noTinNumberReason: string | null;
    noTinNumberOtherReason: string | null;
    polandTaxOfficeId: string | number | null;
    polandTaxOfficeName: string | null;
    polandTaxResidentPesel: string | null;
    emptyTinNumber: string | boolean | null;
}

export default defineComponent({
    name: 'TaxResidenceField',
    components: {
        Field,
        NoTinField,
        TheCountrySelect,
        PolandTaxResidenceField,
        CheckBox,
    },
    props: {
        name: {
            type: String,
            required: true,
        },
        passDefaultTaxCountry: {
            type: String,
            default: '',
        },
        passDefaultTinNumber: {
            type: String,
            default: '',
        },
        countryDisable: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input', 'validate-tin'],
    setup(props, { emit }) {
        const store = useStore();
        const TRANSLATION_KEY = 'compositeFields.TaxResidenceField';

        const formData = reactive<TaxResidenceFormData>({
            taxCountry: null,
            tinNumber: null,
            noTinNumber: null,
            noTinNumberReason: null,
            noTinNumberOtherReason: null,
            polandTaxOfficeId: null,
            polandTaxOfficeName: null,
            polandTaxResidentPesel: null,
            emptyTinNumber: null,
        });

        const tinIgnoreCountries = [] as const;

        const isPolandTaxCountry = ref(false);
        const isNumericalTIN = ref(false);
        const taxOfficeDataForeigner = ref({});

        const countries = computed(() => store.state.config.countries);
        const tinCountries = computed(() => store.state.config.tinCountries);

        const setNumericalTIN = () => {
            if (!formData.taxCountry) {
                isNumericalTIN.value = false;
                return;
            }
            const numericCountries =
                'at, be, bg, cz, de, dk, ee, fr, gr, hr, hu, lt, lv, pl, pt, ro, se, si, sk, us, nl, lu';
            isNumericalTIN.value = numericCountries.includes(
                formData.taxCountry.toLowerCase()
            );
        };

        const setTaxCountry = () => {
            if (props.passDefaultTaxCountry) {
                formData.taxCountry = props.passDefaultTaxCountry;
            }
            if (props.passDefaultTinNumber) {
                formData.tinNumber = props.passDefaultTinNumber;
            }
            setNumericalTIN();
        };

        const mergeFormData = (data: any) => {
            Object.assign(formData, data);

            if (data.noTinNumber) {
                formData.tinNumber = '';
            }
            emit('input', formData);
        };

        const addEmptyTinValue = () => {
            formData.tinNumber = '';
            emit('input', formData);
        };

        const receiveDataPL = (data: any) => {
            Object.assign(formData, data);
            emit('input', formData);
        };

        const emitValidateTin = () => {
            emit('validate-tin', {
                name: props.name,
                tinNumber: formData.tinNumber,
                taxCountry: formData.taxCountry,
            });
        };

        watch(
            () => formData.taxCountry,
            (newValue) => {
                isPolandTaxCountry.value = newValue === 'PL';
                if (tinCountries.value.includes(newValue)) {
                    formData.noTinNumber = '';
                }
                setNumericalTIN();
                emit('input', formData);
            }
        );

        watch(
            () => formData.tinNumber,
            () => {
                emit('input', formData);
            }
        );

        watch(
            () => formData.emptyTinNumber,
            () => {
                emit('input', formData);
            }
        );

        onMounted(() => {
            setTaxCountry();
        });

        return {
            TRANSLATION_KEY,
            formData,
            isPolandTaxCountry,
            isNumericalTIN,
            taxOfficeDataForeigner,
            countries,
            tinCountries,
            tinIgnoreCountries,
            setTaxCountry,
            mergeFormData,
            receiveDataPL,
            addEmptyTinValue,
            emitValidateTin,
        };
    },
});
</script>

<style scoped>
.personal-id-number-description {
    margin-bottom: 10px;
}
.empty-tin-desc {
    font-size: 12px;
    margin-top: 5px;
}
</style>
